import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import { Col, Row, Table } from "reactstrap";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { isTrue } from "../utils/helpers";

const normalizeLegs = (legs) => {
  if (Array.isArray(legs)) return legs;

  if (typeof legs === 'string') {
    try {
      const parsed = JSON.parse(legs);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }

  return [];
};

const normalizeAssetList = (value) => {
  if (Array.isArray(value)) {
    return value.map(getLegAsset).filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};



const getLegAsset = (leg) =>
  leg?.asset || leg?.currency || leg?.code || leg?.name || '';

const getLegGroups = (doc) => {
  const normalized = normalizeLegs(doc?.legs);
  const longFromFields = normalizeAssetList(
    doc?.long_legs || doc?.long_assets || doc?.long
  );
  const shortFromFields = normalizeAssetList(
    doc?.short_legs || doc?.short_assets || doc?.short
  );

  return {
    long: longFromFields.length
      ? longFromFields
      : normalized
      .filter((leg) => String(leg?.side).toUpperCase() === 'LONG')
      .map(getLegAsset)
      .filter(Boolean),
    short: shortFromFields.length
      ? shortFromFields
      : normalized
      .filter((leg) => String(leg?.side).toUpperCase() === 'SHORT')
      .map(getLegAsset)
      .filter(Boolean),
  };
};

const getSignal = (doc, isCarryTrade) => {
  if (doc?.signal) return doc.signal;

  if (isCarryTrade && doc?.long_count && doc?.short_count) {
    return `LONG ${doc.long_count} / SHORT ${doc.short_count}`;
  }

  return 'NA';
};

function RecentForecastTable(props) {
  const [data, setData] = useState([]);
  const [isLoader, setIsLoader] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const isCarryTrade = isTrue(props?.asset?.is_carry_trade);

  const toggleRow = (id) => {
    setExpandedRows((current) => ({
      ...current,
      [id]: !current[id],
    }));
  };

  const fetchData = useCallback(async (type) => {
    try {
      let response;
      if (type === "assets") {
        response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/assets/${props?.id}/forecasts?skip=0&limit=10`
        );
      } else {
        response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/portfolios/${props?.id}/forecasts?skip=0&limit=10`
        );
      }
      if (response.status === 200) {
        setIsLoader(false);
        setData(response?.data);
      }
    } catch (error) {
      setIsLoader(false);
      console.error("Error fetching data:", error);
    }
  }, [props?.id]);

  const fetchPortFolioData = useCallback(async () => {
    if (props.resourceType) {
      setIsLoader(true);
      await fetchData(props.resourceType);
      return;
    }

    try {
      setIsLoader(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/portfolios/?skip=0&limit=1000`
      );
      if (response.status === 200) {
        const findPortfolio =
          response?.data?.data?.length &&
          response.data.data.find((data) => data.id === props.id);
        fetchData(findPortfolio ? "portfolio" : "assets");
      }
    } catch (error) {
      setIsLoader(false);
      console.error("Error fetching portfolio data:", error);
      return [];
    }
  }, [props.id, props.resourceType, fetchData]);

  useEffect(() => {
    if (props?.id) {
      fetchPortFolioData();
    }
  }, [props?.id, fetchPortFolioData]);

  const filtered = data;

  // TODO: Filter data based on forecastTerm

  return (
    <Row className="p-3">
      {isLoader ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 400,
            width: '100%',
          }}
        >
          <ClipLoader size={45} color="#005687" />
        </div>
      ) : (
        <Col>
          <Table striped bordered responsive className="forecasts-table">
            <thead>
              <tr>
                <th>Signal</th>
                <th>Return</th>
                <th>Date of Signal</th>
                <th>End of Signal</th>
              </tr>
            </thead>
            <tbody>
              {data && filtered.length > 0 ? (
                <>
                  {filtered.map((doc, index) => {
                    const rowId = doc.id || `${doc.date_of_forecast}-${index}`;
                    const legGroups = getLegGroups(doc);
                    const hasLegs =
                      isCarryTrade &&
                      (legGroups.long.length > 0 || legGroups.short.length > 0);
                    const isExpanded =
                      expandedRows[rowId] === undefined
                        ? index === 0
                        : expandedRows[rowId];
                    const signal = getSignal(doc, isCarryTrade);

                    return (
                      <React.Fragment key={rowId}>
                        <tr>
                          <td>
                            <button
                              type="button"
                              className="forecast-signal-button"
                              disabled={!hasLegs}
                              onClick={() => hasLegs && toggleRow(rowId)}
                            >
                              <span
                                className={
                                  signal === 'LONG'
                                    ? 'mb-4 green-text'
                                    : signal === 'SHORT'
                                      ? 'mb-4 red-text'
                                      : 'mb-4'
                                }
                              >
                                {signal}
                              </span>
                              {hasLegs &&
                                (isExpanded ? (
                                  <ExpandLess className="forecast-signal-icon" />
                                ) : (
                                  <ExpandMore className="forecast-signal-icon" />
                                ))}
                            </button>
                          </td>
                          <td>
                            <span
                              className={
                                parseFloat(doc.interest) >= 0
                                  ? 'green-text'
                                  : 'red-text'
                              }
                            >
                              {doc.interest}%
                            </span>
                          </td>
                          <td>
                            {doc.date_of_forecast
                              ? new Date(doc.date_of_forecast).toLocaleDateString(
                                  'en-US',
                                )
                              : 'NA'}
                          </td>
                          <td>
                            {doc.date_of_exit
                              ? new Date(doc.date_of_exit).toLocaleDateString(
                                  'en-US',
                                )
                              : ''}
                          </td>
                        </tr>
                        {hasLegs && isExpanded && (
                          <tr className="forecast-legs-row">
                            <td colSpan="4">
                              {legGroups.long.length > 0 && (
                                <>
                                  <strong>Long:</strong>{' '}
                                  {legGroups.long.join(', ')}
                                </>
                              )}
                              {legGroups.long.length > 0 &&
                                legGroups.short.length > 0 && (
                                  <span className="forecast-leg-separator">
                                    ·
                                  </span>
                                )}
                              {legGroups.short.length > 0 && (
                                <>
                                  <strong>Short:</strong>{' '}
                                  {legGroups.short.join(', ')}
                                </>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </>
              ) : (
                <tr>
                  <td colSpan="4">Coming Soon</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Col>
      )}
    </Row>
  );
}

export default RecentForecastTable;
